import React, { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Upload, Calendar, CheckCircle } from "lucide-react";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddSupplierItem from "./AddSupplierItem";
import FormItems from "./FormItems";
import BackBtn from "../../widgets/BackBtn";
import LogoutButton from "../../widgets/LogoutButton";
import SupplierItems from "./SupplierItems";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const OesDataForm = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const events = [
    {
      id: "imtex2025",
      name: "IMTEX 2025",
      description: " innovations in manufacturing technology",
    },
    {
      id: "dmtx2025-imtma",
      name: "DMTX 2025 - IMTMA",
      description: "Delhi Machine Tool Expo 2025",
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
    setSelectedEvent(null);
    setImportResult(null);
  };

  const handleEventSelect = (eventId) => {
    setSelectedEvent(eventId);
  };

  const handleImportConfirm = async () => {
    if (!selectedEvent) return;

    setLoading(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_ENDPOINT}admin/import-oes-forms`, {
        method: "POST",
        ...getHeader(ADMIN),
        body: JSON.stringify({ eventId: selectedEvent }),
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult({
          success: true,
          message: data.message || "Import successful!",
        });
        // Optionally refresh your data here
        setTimeout(() => {
          setShowImportDialog(false);
          setImportResult(null);
          document.location.reload();
        }, 2000);
      } else {
        setImportResult({
          success: false,
          message: data.error || "Import failed",
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box maxWidth={1000} m={"16px auto"}>
        <BackBtn bstyle={{ position: "fixed", left: 2, top: 2 }} />
        <LogoutButton bstyle={{ position: "fixed", top: 2, right: 2 }} />

        <RdMyModal
          showModal={showAddSupplier}
          setShowModal={setShowAddSupplier}
          modalC={<AddSupplierItem />}
        />

        <Typography textAlign={"center"} variant="h5">
          Form name and suppliers
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Upload size={18} />}
            onClick={handleImportClick}
            sx={{ textTransform: "none" }}
          >
            Import from Previous Events
          </Button>
        </Box>

        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="Add Form Names" />
          <Tab label="Add Suppliers" />
        </Tabs>

        <Box sx={{ padding: 2 }}>
          {tabIndex === 0 && <FormItems />}
          {tabIndex === 1 && <SupplierItems />}
        </Box>

        {/* Import Dialog */}
        <Dialog
          open={showImportDialog}
          onClose={() => !loading && setShowImportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Upload size={24} />
              Import from Previous Events
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an event to import form names and suppliers from:
            </Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action will replace the current
                form names and suppliers with those from the selected event.
                Existing data cannot be recovered.
              </Typography>
            </Alert>

            {importResult && (
              <Alert
                severity={importResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                {importResult.message}
              </Alert>
            )}

            <Grid container spacing={2}>
              {events.map((event) => (
                <Grid item xs={12} key={event.id}>
                  <Card
                    variant={
                      selectedEvent === event.id ? "outlined" : "elevation"
                    }
                    sx={{
                      border:
                        selectedEvent === event.id
                          ? "2px solid #1976d2"
                          : "none",
                      backgroundColor:
                        selectedEvent === event.id ? "#e3f2fd" : "inherit",
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleEventSelect(event.id)}
                      disabled={loading}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Calendar size={20} />
                            <Typography variant="h6">{event.name}</Typography>
                          </Box>
                          {selectedEvent === event.id && (
                            <CheckCircle size={20} color="#1976d2" />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {event.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setShowImportDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportConfirm}
              variant="contained"
              disabled={!selectedEvent || loading}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? "Importing..." : "Import"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OesDataForm;
